import type { ReactNode } from "react";
import { Eye } from "lucide-react";

export type Severity = "allow" | "ask" | "deny" | "crit" | "info";

export interface SecurityEvent {
  id: string;
  ts: string;
  severity: Severity;
  title: string;
  who?: string;
  rule?: string;
  cmd?: string;
}

interface Props {
  event: SecurityEvent;
  action?: ReactNode;
  onOpen?: () => void;
}

function fmtTime(iso: string): string {
  // new Date("") returns Invalid Date *without* throwing — the try/catch
  // never fires, and toLocaleTimeString happily returns the literal string
  // "Invalid Date". Guard explicitly so missing/blank timestamps fall back
  // to a dash (or the raw input) instead of bleeding through the UI.
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso || "—";
  return d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

export function EventRow({ event, action, onOpen }: Props) {
  return (
    <div className="sr-event">
      <div className={`sr-event__bar is-${event.severity}`} />
      <div className="sr-event__when">{fmtTime(event.ts)}</div>
      <div className="sr-event__body">
        <strong>{event.title}</strong>
        {event.who && (
          <small>
            {event.who}
            {event.rule ? ` · ${event.rule}` : ""}
          </small>
        )}
      </div>
      {event.cmd && <div className="sr-event__cmd">{event.cmd}</div>}
      <div className="sr-event__actions">
        {action}
        {onOpen && (
          <button
            type="button"
            className="sr-event__open"
            aria-label="Open event detail"
            onClick={onOpen}
          >
            <Eye size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
